import React from 'react';
import { useSelector } from 'react-redux';
import SummaryItem from './SummaryItem';
import { FormattedMessage } from 'react-intl';

// Components

import SummaryFilter from '@components/ssap/pages/pageIncludes/summary/SummaryFilter';
import PageHeader from '@components/ssap/headers/PageHeader';
import SourcesSummary from './summaryIncludes/SourcesSummary';
import SubHeader from '../../../headers/SubHeader';

// Selectors

import {
    selectIfFinalReviewPageActive,
    selectIfSsapInReviewMode,
    selectIsInlineCurrentIncomeEnabled,
    selectPrimaryTaxFiler,
} from '@reducers/appReducer';

// Utils

import { helperFunctions } from '@utils/tools/helperFunctions';
import { normalizeNameOfPerson } from '@utils/normalize/normalizeNameOfPerson';
import { preventerTools } from '@utils/tools/preventerTools';
import { transformDropdownOptionsToObject } from '@utils/normalize/transformDropdownOptionsToObject';

// Constants

import { DROPDOWN_OPTIONS } from '@constants/ssap/hhmTypes/dropdownOptions';
import { INCOME_TYPES } from '@constants/ssap/hhmTypes/incomeAndExpenceTypes';
import { PAGES } from '@constants/ssap/flows/pagesTypes';

// Types

import { IncomeType, SsapSummaryType } from '@components/ssap/SsapTypes';


const IncomeInformationSummary = props => {
    const NOT_PROVIDED = 'NOT_PROVIDED';

    const {
        coverageYear,
        buttonContainer: ButtonContainer,
        householdMembers,
        indexMapper,
        isState,
        isUnemploymentIncomeAttestationEnabled,
        itfFeatureEnabled,
        pageName,
        previousData,
        taxHousehold,
        taxHousehold: {
            unemploymentIncome
        }
    } = props;

    const primaryTaxFiler = useSelector(selectPrimaryTaxFiler);
    const inlineCurrentIncomeEnabled = useSelector(selectIsInlineCurrentIncomeEnabled);
    const isFinalSummaryPage = useSelector(selectIfFinalReviewPageActive);
    const isSsapInReviewMode = useSelector(selectIfSsapInReviewMode);

    const {
        annualTaxIncome: {
            taxHouseholdIncomeDifferenceReason
        }
    } = preventerTools.provideObject(primaryTaxFiler, { annualTaxIncome: { } });

    const getHouseholdMembersSummaries = () => {
        return householdMembers.map((hhm , hhmIdx)=> {

            const {
                applicantGuid,
                name,
                annualTaxIncome: {
                    projectedIncome,
                    reportedIncome
                },
                incomes,
                expenses,
                taxHouseholdComposition: {
                    independentTaxFiler
                }
            } = hhm;

            const {
                firstName,
                lastName
            } = name;

            const isIndependentTaxFilerWarningIsShown = itfFeatureEnabled
                && independentTaxFiler
                && !!(projectedIncome || reportedIncome)
                && pageName !== PAGES.FINAL_APPLICATION_REVIEW_PAGE;

            const individualReasonsToRender = incomes.reduce((incomesToRender, income, incomeIdx) => {
                return income.incomeDifferenceReason
                    ? [
                        ...incomesToRender,
                        {
                            ...income,
                            incomeIdx
                        }
                    ]
                    : incomesToRender;
            }, []);

            const transformedOptions = transformDropdownOptionsToObject(DROPDOWN_OPTIONS.INCOME_INDIVIDUAL_DISCREPANCIES);

            const getReason = reason => {
                return transformedOptions[reason];
            };

            const getReasonByTypeOrSource = (incomes = [], sourceName, type) => {
                if(type === IncomeType.DUMMY) {
                    const incomeWithDummyType = incomes.find(i => i.type === IncomeType.DUMMY);
                    if(incomeWithDummyType) {
                        return getReason(incomeWithDummyType.incomeDifferenceReason);
                    } else {
                        return NOT_PROVIDED;
                    }
                }

                if(type === IncomeType.JOB) {
                    const incomeWithSameSource = incomes.find(i => i.sourceName === sourceName);
                    if(incomeWithSameSource) {
                        return getReason(incomeWithSameSource.incomeDifferenceReason);
                    } else {
                        return NOT_PROVIDED;
                    }
                }
            };

            const getIndividualReasons = () => {
                if(!inlineCurrentIncomeEnabled) {
                    return null;
                }

                return individualReasonsToRender.map(income => {
                    const {
                        incomeDifferenceReason,
                        sourceName,
                        type
                    } = income;

                    return (
                        <div
                            className = 'grid-row'
                            key = { `${sourceName}_${incomeDifferenceReason}` }
                        >
                            <SummaryItem
                                textCode = 'ssap.income_information.summary.individual_income_discrepancy'
                                replacements = { {
                                    isDummy: type === INCOME_TYPES.DUMMY,
                                    sourceName: <strong>{ sourceName }</strong>
                                } }
                                value = { getReason(incomeDifferenceReason) }
                                desc = {
                                    [
                                        [
                                            [
                                                `taxHousehold.householdMember[${ hhmIdx }].incomes`
                                            ],
                                            getReasonByTypeOrSource,
                                            [
                                                sourceName,
                                                type
                                            ]
                                        ]
                                    ]
                                }
                            />
                        </div>
                    );
                });
            };

            return (
                <SummaryFilter
                    key = { applicantGuid }
                    indexMapper = { indexMapper }
                    summaryId = { SsapSummaryType.INCOME_INFORMATION.processReplacements({ idx: hhmIdx }) }
                >
                    <div className = 'summary__block margin-b-20'>
                        <div className = 'summary__header'>
                            <SubHeader
                                dataTestId={`Income-Info-Header-${applicantGuid}`}
                                text = 'ssap.subheader.non_internationalized_text'
                                textReplacements = { {
                                    text: normalizeNameOfPerson(name)
                                } }
                                headerClassName = 'gi-heading-alt'
                                additionalElement = {(
                                    <ButtonContainer
                                        type = 'editButton_secondary'
                                        args = { [ hhmIdx, PAGES.INCOME_SOURCES_PAGE ] }
                                    />
                                )}
                                additionalElementClassName = 'header-btn'
                            />
                        </div>
                        <div className = 'grid-row' >
                            <SummaryItem
                                category = 'ssap.income_information.summary.yearly_income'
                                replacements = { {
                                    coverageYear
                                } }
                                value = { helperFunctions.getIncomeValueForRender(projectedIncome, reportedIncome) }
                                ignoreIntl
                                id = { `incomeSource_${firstName}_${lastName}` }
                                desc = {
                                    [
                                        [
                                            [
                                                `taxHousehold.householdMember[${ hhmIdx }].annualTaxIncome.projectedIncome`,
                                                `taxHousehold.householdMember[${ hhmIdx }].annualTaxIncome.reportedIncome`
                                            ],
                                            helperFunctions.getIncomeValueForRender
                                        ]
                                    ]
                                }
                            />
                        </div>

                        {
                            getIndividualReasons()
                        }

                        <SourcesSummary
                            { ...{
                                applicantGuid,
                                incomes,
                                expenses,
                                isState,
                                previousData,
                                reportedIncome,
                                isFinalSummaryPage,
                                isSsapInReviewMode
                            } }
                        />

                        {
                            isIndependentTaxFilerWarningIsShown &&

                            <div className = 'usa-alert usa-alert--info gi-alert gi-alert__info'>
                                <div className = 'usa-alert__body gi-alert__info__body'>
                                    <p className = 'usa-alert__text gi-alert__info__text'>
                                        <FormattedMessage
                                            id = 'ssap.income_information.summary.independent_tax_filer'
                                            values = { {
                                                independentTaxFilerName: (
                                                    <b>
                                                        { normalizeNameOfPerson(name) }
                                                    </b>
                                                )
                                            } }
                                        />
                                    </p>
                                </div>
                            </div>
                        }
                    </div>
                </SummaryFilter>
            );
        });
    };

    const getTaxHouseholdIncomeDifferenceReason = taxHousehold => {
        const {
            householdMember,
            primaryTaxFilerPersonId
        } = taxHousehold;

        const primaryTaxFilerIndex = householdMember.findIndex(hhm => hhm.personId === primaryTaxFilerPersonId);

        const {
            annualTaxIncome: {
                taxHouseholdIncomeDifferenceReason
            }
        } = primaryTaxFilerIndex !== -1
            ? householdMember[primaryTaxFilerIndex]
            : { annualTaxIncome: { } };

        if(primaryTaxFilerIndex === -1) {
            return NOT_PROVIDED;
        }

        const transformedOptions = transformDropdownOptionsToObject(DROPDOWN_OPTIONS.INCOME_TAX_HOUSEHOLD_DISCREPANCIES);

        if (taxHouseholdIncomeDifferenceReason === null) {
            return NOT_PROVIDED;
        }

        return transformedOptions[taxHouseholdIncomeDifferenceReason];
    };

    const getTaxHouseholdDiscrepancyBlock = () => {
        if(!inlineCurrentIncomeEnabled || !taxHouseholdIncomeDifferenceReason) {
            return null;
        }

        return (
            <SummaryFilter summaryId = { SsapSummaryType.INCOME_DISCREPANCY } >
                <div
                    className = 'summary__block margin-b-20'
                    key = 'tax_household_summary_block'
                >
                    <div className = 'summary__header'>
                        <SubHeader
                            text = 'ssap.subheader.income_discrepancy'
                            headerClassName = 'gi-heading-alt'
                            additionalElement = {
                                (
                                    <ButtonContainer
                                        type = 'editButton_secondary'
                                        args = { [ undefined, PAGES.INCOME_DISCREPANCY_PAGE ] }
                                    />
                                )
                            }
                            additionalElementClassName = 'header-btn'
                        />
                    </div>
                    <div
                        className = 'grid-row'
                        key = 'tax_household_reason'
                    >
                        <SummaryItem
                            textCode = 'ssap.income_information.summary.tax_household_income_discrepancy'
                            value = { getTaxHouseholdIncomeDifferenceReason(taxHousehold) }
                            id = 'tax_household_reason'
                            desc = {
                                [
                                    [
                                        [
                                            'DATA.TAX_HOUSEHOLD'
                                        ],
                                        getTaxHouseholdIncomeDifferenceReason
                                    ]
                                ]
                            }
                        />
                    </div>
                </div>
            </SummaryFilter>
        );
    };

    return (
        <SummaryFilter summaryId = { SsapSummaryType.INCOME_INFORMATION.processReplacements({ idx: 'ROOT' }) }>
            <div className = 'final-review-heading' >
                <PageHeader
                    text = 'ssap.subheader.income_info'
                    headerClassName = 'gi-heading-alt'
                />
            </div>
            <SummaryFilter summaryId = { SsapSummaryType.UNEMPLOYMENT_ATTESTATION } indexMapper = { indexMapper }>
                {
                    isUnemploymentIncomeAttestationEnabled &&

                    <div className = 'summary__block margin-b-20' >
                        <div className = 'grid-row' >
                            <SummaryItem
                                category = 'ssap.income_information.summary.unemployment_income'
                                value = { unemploymentIncome }
                                replacements = { { coverageYear } }
                                desc = {
                                    [
                                        [
                                            'taxHousehold.unemploymentIncome'
                                        ]
                                    ]
                                }
                            />
                        </div>
                    </div>
                }
            </SummaryFilter>
            { getHouseholdMembersSummaries() }
            { getTaxHouseholdDiscrepancyBlock() }
        </SummaryFilter>
    );
};

export default IncomeInformationSummary;
